import { clsx } from 'clsx'
import { type FC, type ReactElement } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { PrescriptionPrescriptionTypeEnum } from '@redteclab/api/clients/bully'

import { priceGetFormattedPrice } from '../../../../../price'
import { priceAccessorIsPrescription } from '../../../../model/priceAccessor/priceAccessorIsPrescription'
import { type ProductPrices } from '../../../ProductBuyBox/hooks/useProductPrices'
import { ProductReferencePriceLabel } from '../../../ProductPrice/ProductReferencePrice/ProductReferencePriceLabel'

type ProductAttributeTileStrikeThroughPriceProps = {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  isLarge: boolean
  prices: ProductPrices
}

const getStrikeThroughPriceClass = ({
  isLarge,
  isPrescription,
}: {
  isLarge: boolean
  isPrescription: boolean
}): string =>
  clsx(
    'text-s',
    isLarge && 'flex justify-end',
    isPrescription && 'font-medium text-dark-primary-max',
    !isPrescription && 'text-dark-primary-strong',
  )

const getOriginalPrice = ({
  intl,
  prices,
}: {
  intl: IntlShape
  prices: ProductPrices
}): ReactElement | null => {
  const {
    displayPrice,
    isThirtyDayPriceTrendDown,
    referencePriceType,
    shouldUseReferencePrice,
    showThirtyDaysPrice,
    strikeThroughOfferPrice,
    thirtyDaysLowestPrice,
  } = prices

  if (shouldUseReferencePrice) {
    return (
      <span className="text-s">
        <ProductReferencePriceLabel referencePriceType={referencePriceType} />{' '}
        {priceGetFormattedPrice(strikeThroughOfferPrice?.price ?? 0, intl)}
      </span>
    )
  }

  if (showThirtyDaysPrice && isThirtyDayPriceTrendDown) {
    return (
      <span className="flex flex-col gap-x-1 text-xs">
        <span>
          <FormattedMessage id="general.label.thirtyDaysPrice" />
        </span>
        <span>
          {priceGetFormattedPrice(thirtyDaysLowestPrice?.amount ?? 0, intl)}
        </span>
      </span>
    )
  }

  const displayPriceValue = displayPrice?.price ?? 0
  const strikeThroughOfferPriceValue = strikeThroughOfferPrice?.price ?? 0

  if (displayPriceValue < strikeThroughOfferPriceValue) {
    return (
      <span className="flex flex-col gap-x-1 text-xs">
        <span
          // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
          className="flex h-[18px] w-full"
        />
        <span
          className={clsx(
            strikeThroughOfferPriceValue !== displayPrice && 'line-through',
          )}
        >
          {priceGetFormattedPrice(strikeThroughOfferPrice?.price ?? 0, intl)}
        </span>
      </span>
    )
  }

  return (
    <span className="flex flex-col gap-x-1 text-xs">
      <span
        // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
        className="flex h-[18px] w-full"
      />
      <span
        // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
        className="flex h-[18px] w-full"
      />
    </span>
  )
}

export const ProductAttributeTileStrikeThroughPrice: FC<
  ProductAttributeTileStrikeThroughPriceProps
> = ({ currentPrescriptionType, isLarge, prices }) => {
  const intl = useIntl()

  const isPrescription = priceAccessorIsPrescription(currentPrescriptionType)
  const isPrescriptionButNotPrivate =
    isPrescription &&
    currentPrescriptionType !==
      PrescriptionPrescriptionTypeEnum.PrivatePrescription

  return (
    <li
      className={getStrikeThroughPriceClass({
        isLarge,
        isPrescription,
      })}
    >
      {isPrescriptionButNotPrivate ? (
        <span className="text-xs">
          <FormattedMessage id="general.label.additionalCharge" />
        </span>
      ) : null}
      {isPrescription
        ? null
        : getOriginalPrice({
            intl,
            prices,
          })}
    </li>
  )
}
